import React from 'react';
import { currentLocale } from '../i18n';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import '../styles/research-share-dievo.css';

/* Chart primitives and palettes of the DIEVO research redesign (studio.html).
   Shared by the Share page (ResearchShareDievo) and the in-app results view
   (ResearchDashboardDievo) so the two never drift apart. Every class is
   scoped under .research-share-v2, so the Sense edition is never touched. */

export interface AnswerResult {
  id: number;
  answer_text: string;
  answer_image?: string | null;
  count: number;
  percentage: number;
}
export interface DemographicBreakdown {
  group: string;
  answers: { answer_text: string; percentage: number }[];
}
export interface QuestionResult {
  id: number;
  position: number;
  question_text: string;
  question_type: string;
  answers: AnswerResult[];
  demographic_breakdown: DemographicBreakdown[];
}
export interface ResearchData {
  id: number;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  respondent_count: number;
  current_respondents: number;
  questions: QuestionResult[];
  demographics: {
    age: { range: string; count: number; percentage: number }[];
    gender: { label: string; count: number; percentage: number }[];
    geo: { region: string; count: number; percentage: number }[];
  };
  progress?: {
    launched_at: string;
    original_launched_at: string;
    duration_days_min: number;
    duration_days_max: number;
    is_complete: boolean;
  };
}

export const imgUrl = (u?: string | null) => u ? `${API_BASE_URL.replace(/\/$/, '')}${u}` : '';

// Palettes — the six chart colours of studio.html, in its own order:
// lime, violet, blue, amber, coral, mint. Each chart starts where the mockup
// starts it: the passport donuts on lime, the answers on mint/amber (its "так"
// and "ні"), which is also what the stacked bars use for the same answers.
const STUDIO = {
  lime: '#C8F032', violet: '#6B4FD8', blue: '#3D6BE0',
  amber: '#F59E0B', coral: '#D85A4A', mint: '#1E9E73',
};
// Past the sixth option the mockup has nothing to copy; these two come from the
// same design system (--i-amber, a teal of the same family) and only ever show
// up on a question with more than six answers.
const OVERFLOW = ['#C98B1B', '#0EA5A5'];

export const ANSWER_COLORS = [STUDIO.mint, STUDIO.amber, STUDIO.blue, STUDIO.violet, STUDIO.coral, STUDIO.lime, ...OVERFLOW];
export const AGE_COLORS = [STUDIO.lime, STUDIO.violet, STUDIO.blue, STUDIO.amber, STUDIO.coral, STUDIO.mint];
export const GENDER_COLORS = [STUDIO.lime, STUDIO.violet];
export const GEO_COLORS = [STUDIO.lime, STUDIO.violet, STUDIO.blue, STUDIO.amber, STUDIO.coral, STUDIO.mint, ...OVERFLOW];

// One hover card for every chart here. It is positioned against the chart's own
// box (each container is position:relative) and flips below the cursor near the
// top edge, because the question card clips what overflows it.
function useTip(containerClass: string) {
  const [tip, setTip] = React.useState<
    { x: number; y: number; below: boolean; node: React.ReactNode } | null>(null);

  const show = (e: React.MouseEvent, node: React.ReactNode) => {
    const box = (e.currentTarget as Element).closest('.' + containerClass)?.getBoundingClientRect();
    if (!box) return;
    const y = e.clientY - box.top;
    setTip({ x: e.clientX - box.left, y, below: y < 70, node });
  };

  const el = tip ? (
    <div className={`rsd-tip ${tip.below ? 'rsd-tip-below' : ''}`}
      style={{ left: tip.x, top: tip.y }}>{tip.node}</div>
  ) : null;

  return { show, hide: () => setTip(null), el };
}

// Head line + one coloured value line — the shape every tooltip here uses.
const TipBody: React.FC<{ head: string; color?: string; label?: string; value: string }> =
  ({ head, color, label, value }) => (
    <>
      <div className="rsd-tip-head">{head}</div>
      <div className="rsd-tip-row">
        {color && <span className="rsd-tip-dot" style={{ background: color }} />}
        {label && <span className="rsd-tip-label">{label}</span>}
        <span className="rsd-tip-value">{value}</span>
      </div>
    </>
  );

// Ring rsd-donut (studio.html renderDonut): stroke-dasharray segments + centred Total/N.
export const Donut: React.FC<{ data: { value: number; color: string; label?: string }[]; totalLabel: string; size?: number }> = ({ data, totalLabel, size = 200 }) => {
  const tip = useTip('rsd-donut');
  const cx = size / 2, cy = size / 2, r = 64, sw = 38;
  const total = data.reduce((s, d) => s + d.value, 0);
  const C = 2 * Math.PI * r;
  const gap = data.length > 1 ? 2 : 0;
  let offset = 0;
  const segs: React.ReactNode[] = [];
  // Percentages sit on the ring itself for the big slices, so the split reads
  // without hovering; anything under LABEL_MIN_PCT stays clean (tooltip only).
  const labels: React.ReactNode[] = [];
  const LABEL_MIN_PCT = 8;
  data.forEach((d, i) => {
    const share = total > 0 ? d.value / total : 0;
    const rawLen = share * C;
    const len = Math.max(0, rawLen - gap);
    const pctText = `${(share * 100).toFixed(1)}%`;
    segs.push(
      <circle key={i} className="rsd-seg" cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={sw}
        strokeDasharray={`${len.toFixed(2)} ${(C - len).toFixed(2)}`} strokeDashoffset={(-offset).toFixed(2)}
        transform={`rotate(-90 ${cx} ${cy})`}
        onMouseMove={(e) => tip.show(e, (
          <TipBody head={d.label || totalLabel} color={d.color}
            value={`${d.value.toLocaleString(currentLocale())} · ${pctText}`} />
        ))}
        onMouseLeave={tip.hide} />
    );
    const pct = share * 100;
    if (pct >= LABEL_MIN_PCT && data.length > 1) {
      // The label goes on the middle of the slice AS PAINTED, which is not the
      // middle of the dash: stroke-linecap:round adds a cap of sw/2 to both ends
      // and each slice is drawn over the previous one's end cap. So every slice
      // except the first and the last (nothing paints over their outer ends)
      // reads one cap earlier than its dash says — put the label there, or it
      // drifts toward the clockwise edge.
      const cap = sw / 2;
      const isEdge = i === 0 || i === data.length - 1;
      const midArc = isEdge ? offset + (rawLen - gap) / 2 : offset + rawLen / 2 - cap;
      // stroke starts at 12 o'clock (rotate -90) and runs clockwise
      const midRad = (midArc / C) * 2 * Math.PI - Math.PI / 2;
      labels.push(
        <text key={`l${i}`} className="rsd-seg-label"
          x={cx + r * Math.cos(midRad)} y={cy + r * Math.sin(midRad)}
          textAnchor="middle" dominantBaseline="central">{pct.toFixed(0)}%</text>
      );
    }
    offset += rawLen;
  });
  return (
    <div className="rsd-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segs}
        {labels}
        <text x={cx} y={cy - 8} textAnchor="middle" className="rsd-total-label">{totalLabel}</text>
        <text x={cx} y={cy + 18} textAnchor="middle" className="rsd-total-value">{total.toLocaleString(currentLocale())}</text>
      </svg>
      {tip.el}
    </div>
  );
};

export const DonutCard: React.FC<{ title: string; data: { label: string; value: number; color: string }[]; maxLegendItems?: number }> = ({ title, data, maxLegendItems }) => {
  const { t } = useTranslation();
  const legendData = maxLegendItems && data.length > maxLegendItems
    ? [...data.slice(0, maxLegendItems), { label: t('research.others'), value: data.slice(maxLegendItems).reduce((s, x) => s + x.value, 0), color: '#A8A8A3' }]
    : data;
  return (
    <div className="rsd-donut-chart-container">
      <h4 className="rsd-donut-chart-title">{title}</h4>
      <div className="rsd-donut-chart-wrapper">
        {/* rsd-donut renders the SAME rolled-up buckets as the legend (top-N + Others)
            so geo with many regions stays a clean ring, not a mess of tiny arcs */}
        <Donut data={legendData.map(d => ({ value: d.value, color: d.color, label: d.label }))} totalLabel={t('research.respondentsTotal')} size={180} />
      </div>
      <div className="rsd-donut-legend">
        {legendData.map((item, index) => (
          <div key={index} className="rsd-donut-legend-item">
            <span className="rsd-donut-legend-color" style={{ background: item.color }} />
            <span className="rsd-donut-legend-label">{item.label}</span>
            <span className="rsd-donut-legend-value">{item.value.toLocaleString(currentLocale())}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HorizontalBarChart: React.FC<{ data: { label: string; percentage: number; color: string }[] }> = ({ data }) => {
  const tip = useTip('rsd-horizontal-bar-chart');

  // Multiple-choice shares rarely come near 100%, and a track scaled to 100
  // turned every answer into the same short stub — the eye could not rank them.
  // Scale to the leading answer instead (rounded up to a tidy step): the bars
  // become comparable and the exact number is printed beside each one anyway.
  const top = Math.max(...data.map(d => d.percentage), 0);
  const scaleMax = Math.min(100, Math.max(10, Math.ceil(top / 10) * 10));

  return (
    <div className="rsd-horizontal-bar-chart">
      {data.map((item, index) => (
        <div key={index} className="rsd-horizontal-bar-row">
          {/* The label sits in its own column on the left: long statements get
              cut with an ellipsis, and the full text comes back on hover. */}
          <div className="rsd-horizontal-bar-label"
            onMouseMove={(e) => tip.show(e, (
              <TipBody head={item.label} color={item.color}
                value={`${item.percentage.toFixed(1)}%`} />
            ))}
            onMouseLeave={tip.hide}>{item.label}</div>
          <div className="rsd-horizontal-bar-track">
            <div className="rsd-horizontal-bar-fill"
              style={{ width: `${(item.percentage / scaleMax) * 100}%`, backgroundColor: item.color }} />
          </div>
          <span className="rsd-horizontal-bar-value">{item.percentage.toFixed(1)}%</span>
        </div>
      ))}
      {tip.el}
    </div>
  );
};

// A 160px column: below ~10% a segment is thinner than the type, so it keeps
// the tooltip only and the label is dropped.
const SEGMENT_LABEL_MIN_PCT = 10;

export const StackedBarChart: React.FC<{ breakdown: DemographicBreakdown[]; answers: AnswerResult[] }> = ({ breakdown, answers }) => {
  const tip = useTip('rsd-stacked-bar-chart');
  const answerColors = answers.reduce((acc, answer, index) => {
    acc[answer.answer_text] = ANSWER_COLORS[index % ANSWER_COLORS.length];
    return acc;
  }, {} as Record<string, string>);
  return (
    <div className="rsd-stacked-bar-chart">
      <div className="rsd-stacked-bar-axis"><span>100%</span><span>75%</span><span>50%</span><span>25%</span><span>0%</span></div>
      <div className="rsd-stacked-bars">
        {breakdown.map((group, groupIndex) => (
          <div key={groupIndex} className="rsd-stacked-bar-column">
            <div className="rsd-stacked-bar">
              {/* reversed: first answer sits at the BOTTOM (base), like the original */}
              {[...group.answers].reverse().map((answer, answerIndex) => (
                <div key={answerIndex} className="rsd-stacked-bar-segment"
                  style={{ height: `${answer.percentage}%`, background: answerColors[answer.answer_text] || ANSWER_COLORS[answerIndex % ANSWER_COLORS.length] }}
                  onMouseMove={(e) => tip.show(e, (
                    <TipBody head={group.group}
                      color={answerColors[answer.answer_text] || ANSWER_COLORS[answerIndex % ANSWER_COLORS.length]}
                      label={answer.answer_text}
                      value={`${answer.percentage.toFixed(1)}%`} />
                  ))}
                  onMouseLeave={tip.hide}>
                  {answer.percentage >= SEGMENT_LABEL_MIN_PCT && (
                    <span className="rsd-stacked-bar-value">{answer.percentage.toFixed(0)}%</span>
                  )}
                </div>
              ))}
            </div>
            <span className="rsd-stacked-bar-label">{group.group}</span>
          </div>
        ))}
      </div>
      {tip.el}
    </div>
  );
};

export const fmtDate = (s?: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }) =>
  s ? new Date(s).toLocaleDateString(currentLocale(), opts) : '';

// DD.MM.YYYY (matches the studio.html "Generated · 21.05.2026" format)
export const fmtDotted = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
